"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Star, MapPin, Quote, Loader, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
function ReviewForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="size-16 rounded-full bg-green-100 flex items-center justify-center">
          <CircleCheck className="size-8 text-green-600" />
        </div>
        <h3 className="text-xl font-serif font-bold text-black">Review Submitted</h3>
        <p className="text-black max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Write Another Review
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-black">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormDescription className="text-black">Enter the name you’d like displayed.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="location" rules={{
        required: "Location is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-black">Location (City)</FormLabel>
              <FormControl>
                <Input placeholder="London" {...field} />
              </FormControl>
              <FormDescription className="text-black">City where the work was performed.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="rating" rules={{
      required: "Please select a rating"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-black">Rating</FormLabel>
            <FormControl>
              <div className="flex items-center gap-4">
                {[1, 2, 3, 4, 5].map(value => <label key={value} className="flex flex-col items-center gap-1 cursor-pointer group">
                    <input type="radio" className="sr-only" name={field.name} value={value.toString()} checked={field.value === value.toString()} onChange={field.onChange} />
                    <Star className={`size-8 transition-all duration-200 ${Number(field.value) >= value ? "fill-[var(--ring)] text-[var(--ring)]" : "text-muted-foreground/30 group-hover:text-[var(--ring)]/50"}`} />
                    <span className="text-xs font-medium text-black">
                      {value}
                    </span>
                  </label>)}
              </div>
            </FormControl>
            <FormDescription className="text-black">Select a rating from 1 (lowest) to 5 (highest).</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="review_text" rules={{
      required: "Review text is required",
      minLength: {
        value: 20,
        message: "Review must be at least 20 characters"
      },
      maxLength: {
        value: 1000,
        message: "Review cannot exceed 1000 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-black">Your Review</FormLabel>
            <FormControl>
              <Textarea placeholder="Share your experience with SimProTiling..." className="min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-black">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-[var(--ring)] hover:bg-[var(--ring)]/90 text-white font-medium py-6">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Submitting Review...
          </> : "Submit Review"}
      </Button>
    </Form>;
}
export default function CustomerReviews() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const reviews = [{
    id: 1,
    name: "Sarah Jenkins",
    location: "Kensington, London",
    rating: 5,
    text: "SimProTiling transformed our master bathroom into a sanctuary. The attention to detail with the marble tiling was exceptional. They were punctual, clean, and incredibly professional throughout the entire renovation.",
    date: "2 weeks ago",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/avatar_sarah_jenkins.png"
  }, {
    id: 2,
    name: "James Alistair",
    location: "Fulham, London",
    rating: 5,
    text: "I hired them for a wet room installation and I couldn't be happier. The tanking system they used gives me complete peace of mind, and the finish is hotel-quality. Highly recommend for anyone looking for premium work.",
    date: "1 month ago",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/avatar_james_alistair.png"
  }, {
    id: 3,
    name: "Elena Rossi",
    location: "Chelsea, London",
    rating: 5,
    text: "Professional from start to finish. The team helped us select the right porcelain tiles for our kitchen floor and the installation was flawless. The mitred edges are sharp and the grout lines are perfect.",
    date: "2 months ago",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/avatar_elena_rossi.png"
  }];
  return <section id="customerreviews" className="py-20 md:py-28 bg-background relative overflow-hidden" ref={ref}>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-black mb-4 tracking-tight">
            Client Testimonials
          </h2>
          <div className="w-24 h-1 bg-[var(--ring)] mx-auto mb-6" />
          <p className="text-lg text-black leading-relaxed">
            We take pride in delivering exceptional quality and service. Here is what our
            clients across London have to say about their experience with SimProTiling.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {reviews.map((review, index) => <div key={review.id} data-index={index} className={`h-full transition-all duration-700 delay-[${index * 150}ms] transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-300 rounded-sm flex flex-col">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => <Star key={i} className={`size-4 ${i < review.rating ? "fill-[var(--ring)] text-[var(--ring)]" : "text-muted-foreground/30"}`} />)}
                    </div>
                    <Quote className="size-8 text-[var(--ring)]/20" />
                  </div>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-black italic leading-relaxed">
                    "{review.text}"
                  </p>
                </CardContent>
                <CardFooter className="pt-4 border-t border-border/50 mt-auto">
                  <div className="flex items-center gap-3 w-full">
                    <SafeHydrate><Avatar className="size-10 border border-border">
                      <AvatarImage src={review.avatar} alt={review.name} />
                      <AvatarFallback className="bg-primary/10 text-black font-serif">
                        {review.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div>
                      <h4 className="font-serif font-semibold text-black text-sm">
                        {review.name}
                      </h4>
                      <div className="flex items-center gap-1 text-xs text-black">
                        <MapPin className="size-3" />
                        <span>{review.location}</span>
                      </div>
                    </div>
                  </div>
                </CardFooter>
              </Card>
            </div>)}
        </div>

        <div className={`text-center transition-all duration-700 delay-500 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Dialog>
            <DialogTrigger asChild>
              <Button size="lg" className="bg-[var(--ring)] hover:bg-[var(--ring)]/90 text-white font-semibold px-8 py-6 text-lg rounded-sm shadow-lg hover:shadow-xl transition-all">
                Leave a Review
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg rounded-sm border-border">
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif text-black text-center">
                  Leave a Review
                </DialogTitle>
                <DialogDescription className="text-center text-black">
                  Share your experience with SimProTiling. We value your feedback.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <FormProvider formId="review_form_home" sectionName="customer-reviews" defaultValues={{
                name: "",
                location: "",
                rating: "5",
                review_text: ""
              }} validationRules={{
                name: {
                  required: true
                },
                location: {
                  required: true
                },
                rating: {
                  required: true
                },
                review_text: {
                  required: true,
                  minLength: 20,
                  maxLength: 1000
                }
              }} submitText="Submit Review" successMessage="Thank you for your review! It has been submitted successfully.">
                  <ReviewForm />
                </FormProvider>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>;
}